'use client';

import { useCallback, useState } from 'react';

import type { ChatMessage, TaskFile } from '@/entities/interview-task';

export type UseEditorDraftsResult = {
	editorDrafts: Record<string, string>;
	setEditorDraft: (fileName: string, code: string) => void;
	resetEditorDraft: (file: TaskFile) => void;
	clearAllDrafts: () => void;
	getDraft: (file: TaskFile) => string;
	isDirty: (file: TaskFile | null) => boolean;
	versionFor: (messages: ChatMessage[], name: string) => number;
};

const getCode = (file: TaskFile): string =>
	file.kind === 'edit' || file.kind === 'view' ? file.code : '';

const getBase = (file: TaskFile): string =>
	file.kind === 'edit' || file.kind === 'view' ? file.baseCode : '';

export function useEditorDrafts(): UseEditorDraftsResult {
	const [editorDrafts, setEditorDrafts] = useState<Record<string, string>>({});

	const setEditorDraft = useCallback((fileName: string, code: string) => {
		setEditorDrafts((d) => ({ ...d, [fileName]: code }));
	}, []);

	const resetEditorDraft = useCallback((file: TaskFile) => {
		const base = getBase(file);
		setEditorDrafts((d) => ({ ...d, [file.name]: base }));
	}, []);

	const clearAllDrafts = useCallback(() => {
		setEditorDrafts({});
	}, []);

	const getDraft = useCallback(
		(file: TaskFile): string => editorDrafts[file.name] ?? getCode(file),
		[editorDrafts],
	);

	const isDirty = useCallback(
		(file: TaskFile | null): boolean => {
			if (!file || file.kind !== 'edit') return false;
			return getDraft(file) !== file.code;
		},
		[getDraft],
	);

	const versionFor = useCallback(
		(messages: ChatMessage[], name: string): number => {
			let max = 0;
			for (const m of messages) {
				for (const f of m.attaches || []) {
					if (f.name === name && 'version' in f) {
						max = Math.max(max, f.version);
					}
				}
			}
			return max + 1;
		},
		[],
	);

	return {
		editorDrafts,
		setEditorDraft,
		resetEditorDraft,
		clearAllDrafts,
		getDraft,
		isDirty,
		versionFor,
	};
}
